/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useCallback, useEffect, useRef, useState} from 'react'
import clsx from 'clsx'
import {useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Controller, useForm} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {consoleLog} from 'utils/utility'
import UiTabSwitcher, {UiTabSwitcherItem} from 'components/Ui/UiTabSwitcher'
import {toast} from 'utils/toastUtils'
import {closeSecondaryModal} from 'helpers/actions'
import {BookingSellerDuplicateOfferInputs, useBookingSellerDuplicateOfferSchema,} from 'pages/Front/Booking/Seller/schemas/useBookingSellerDuplicateOfferSchema'
import UiFormInner from 'components/Forms/UIFormInner'
import UiFormActionsBar from 'components/Forms/UiFormActionsBar'
import {useAppDispatch} from 'helpers/store'
import {OffersAction} from 'store/offers/OffersAction'
import {useTypedSelector} from 'helpers/reducers'
import UiDatePicker from 'components/Ui/UiDatePicker'
import UiFormLabel from 'components/Ui/UiFormLabel'
import UiCheckbox from 'components/Ui/UiCheckbox'
import CompanyAutocomplete from 'components/CompanyAutocomplete'
import {CompaniesAction} from 'store/companies/CompaniesAction'
import {Company} from 'types/entities/Company'
import {DATE_FORMATS} from 'constants/DateFormats'
import dayjs from 'dayjs'
import {UiTabVariant} from 'types/enums/UiTabVariant'
import OpportunitiesAction from 'store/opportunities/OpportunitiesAction'
import {Lead} from 'types/entities/Lead'
import {CompanyAddress} from 'types/entities/CompanyAddress'
import {CompanyContact} from 'types/entities/CompanyContact'
import UiSelect, {UiSelectOption} from 'components/Ui/UiSelect'
import CompanyContactAutocomplete from 'components/CompanyContactAutocomplete'
import {LeadCategory} from 'types/enums/LeadCategory'
import {LeadStatus} from 'types/enums/LeadStatus'
import {DEFAULT_HALL_ID} from 'helpers/config'
import {formatAddressSummary} from 'utils/addressUtils'

interface BookingSellerDuplicateOfferModalProps {
    offer_id: number
    title?: string
    options?: UiTabSwitcherItem[]
    defaultOption?: UiTabSwitcherItem
    closeModal?: () => void
}

const BookingSellerDuplicateOfferModal = (props: BookingSellerDuplicateOfferModalProps) => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const { t } = useTranslation()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [companies, setCompanies] = useState<Company[]>([])
    const [selectedCompany, setSelectedCompany] = useState<Company | null>(null)
    const [opportunitySearch, setOpportunitySearch] = useState('')
    const [opportunityResults, setOpportunityResults] = useState<Lead[]>([])
    const [selectedOpportunity, setSelectedOpportunity] = useState<Lead | null>(null)
    const opportunitySearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const [newCompanyAddresses, setNewCompanyAddresses] = useState<CompanyAddress[]>([])
    const [newCompanyContacts, setNewCompanyContacts] = useState<CompanyContact[]>([])

    const BookingSellerDuplicateOfferSchema = useBookingSellerDuplicateOfferSchema()

    const { handleSubmit, setValue, watch, control } = useForm<BookingSellerDuplicateOfferInputs>({
        defaultValues: {
            option: props?.defaultOption?.id ? props.defaultOption.id : undefined,
            datetime: dayjs().toDate(),
            company_id: null,
            package_category_name: null,
            calculations: [],
            lead_id: null,
            address_id: null,
            contact_id: null,
        },
        resolver: zodResolver(BookingSellerDuplicateOfferSchema),
    })

    const selectedOffer = useTypedSelector((state) => state.offersState.selectedOffer)
    const sessionSellerId = useTypedSelector((state) => state.sessionState.session?.uid)



    const fetchCompanies = useCallback(() => {
        // Using fetchCompaniesTablePage as it returns the list
        dispatch(
            CompaniesAction.fetchCompaniesTablePage({
                page: 1,
                per_page: 20,
                search: '',
            })
        )
            .then((res) => {
                setCompanies(res.data)
            })
            .finally(() => {
                
            })
    }, [dispatch])

    const selectedOption = watch('option')
    const calculationsFn = watch('calculations')
    const watchedCompanyId = watch('company_id')

    const isDifferentCompany = !!watchedCompanyId && watchedCompanyId !== selectedOffer?.company_id

    // Search opportunities for the new company when it changes
    useEffect(() => {
        if (!isDifferentCompany || !watchedCompanyId) {
            setOpportunityResults([])
            setSelectedOpportunity(null)
            setValue('lead_id', null)
            return
        }
        if (opportunitySearchTimerRef.current) clearTimeout(opportunitySearchTimerRef.current)
        opportunitySearchTimerRef.current = setTimeout(() => {
            dispatch(
                OpportunitiesAction.fetchLeadsTablePage({
                    page: 1,
                    per_page: 10,
                    search: [
                        { id: 'company_id', value: watchedCompanyId },
                        ...(opportunitySearch ? [{ id: 'global', value: opportunitySearch }] : []),
                    ],
                })
            )
                .then((res) => setOpportunityResults(res.data))
                .catch(() => setOpportunityResults([]))
        }, 300)
        return () => {
            if (opportunitySearchTimerRef.current) clearTimeout(opportunitySearchTimerRef.current)
        }
    }, [watchedCompanyId, opportunitySearch, isDifferentCompany])

    const itemsForOptions: UiTabSwitcherItem[] = props?.options || [
        { id: '1', name: '1' },
        { id: '2', name: '2' },
        { id: '3', name: '3' },
    ]

    const onSubmitHandler = async (data: BookingSellerDuplicateOfferInputs) => {
        setIsSubmitting(true)
        if (!props.offer_id) {
            toast.error(t('Missing offer ID'))
            setIsSubmitting(false)
            return
        }

        try {
            const targetDateTime = dayjs(data.datetime)
            const date = targetDateTime.isValid()
                ? targetDateTime.format(DATE_FORMATS.DATE_YMD)
                : undefined
            const time = targetDateTime.isValid()
                ? targetDateTime.format(DATE_FORMATS.TIME)
                : undefined

            let resolvedLeadId = data.lead_id ?? null

            if (isDifferentCompany && !resolvedLeadId && data.company_id) {
                const createdOpportunity = await dispatch(
                    OpportunitiesAction.fetchCreateOrUpdateLead({
                        name: selectedCompany?.name || selectedOffer?.company?.name || t('New Opportunity'),
                        company_name: selectedCompany?.name || selectedOffer?.company?.name || undefined,
                        company_id: data.company_id,
                        contact_id: data.contact_id ?? undefined,
                        seller_id: selectedOffer?.seller_id || sessionSellerId || undefined,
                        hall_id: DEFAULT_HALL_ID,
                        category: LeadCategory.Opportunity,
                        status: LeadStatus.NewInquiry,
                    })
                )

                resolvedLeadId = createdOpportunity.id
            }

            await dispatch(
                OffersAction.duplicateOfferById({
                    offerId: props.offer_id,
                    count: Number(data.option),
                    date: date,
                    time: time,
                    company_id: data.company_id,
                    package_category_name: data.package_category_name,
                    calculations: data.calculations,
                    lead_id: resolvedLeadId,
                    address_id: data.address_id,
                    contact_id: data.contact_id,
                })
            )

            toast.success(t('The offer has been successfully duplicated.'))
            if (props.closeModal) {
                props.closeModal()
            } else {
                dispatch(closeSecondaryModal())
            }
        } catch (err: unknown) {
            const status = typeof err === 'object' && err !== null && 'status' in err
                ? (err as { status?: number }).status
                : undefined

            if (status === 401) {
                navigate('/', { replace: true })
                return
            }

            toast.error(t('Error upon duplication.'))
        } finally {
            setIsSubmitting(false)
        }
    }

    const onErrorHandler = (data: unknown) => {
        consoleLog('onErrorHandler', data)
    }

    const setSelectedOptionHandler = (value: string | number | undefined) => {
        if (value !== undefined) {
            setValue('option', value)
        }
    }

	useEffect(() => {
		if (selectedOffer) {
            const fallbackDateTime = dayjs()
            const targetDateTime = selectedOffer.packagesCart?.date
                ? dayjs(selectedOffer.packagesCart.date)
                    .hour(selectedOffer.packagesCart.hour ?? fallbackDateTime.hour())
                    .minute(selectedOffer.packagesCart.minute ?? fallbackDateTime.minute())
                    .second(0)
                    .millisecond(0)
                : fallbackDateTime

            setValue('datetime', targetDateTime.toDate())

			setValue('company_id', selectedOffer.company_id)
            setValue('package_category_name', selectedOffer.packagesCart?.category ?? null)
			setValue('calculations', selectedOffer.calculations?.map((c) => c.id) || [])

			// Preload the current company if it exists
			if (selectedOffer.company) {
				setCompanies([selectedOffer.company])
				setSelectedCompany(selectedOffer.company)
			}
		}
	}, [selectedOffer, setValue]);

    // Load initial companies
    useEffect(() => {
        fetchCompanies()
    }, [fetchCompanies])

    return (
        <form
            className={clsx('BookingSellerDuplicateOfferModal flex flex-col grow')}
            onSubmit={handleSubmit(onSubmitHandler, onErrorHandler)}
        >
            <UiFormInner>
                <div className={clsx('rounded-xl p-6')}>
                    <UiTabSwitcher
                        className={clsx('w-full mb-4')}
                        label={t('Select number of copies')}
                        items={itemsForOptions}
                        selected={selectedOption}
                        variant={UiTabVariant.Group}
                        onClick={(id) => setSelectedOptionHandler(id)}
                    />

                    <div className="flex flex-col gap-4">
                        <UiDatePicker
                            control={control}
                            name="datetime"
                            label={t('Target Date & Time')}
                            includeTime
                            format={DATE_FORMATS.DATE_TIME}
                            required
                        />

                        <Controller
                            control={control}
                            name="company_id"
                            render={({ field, fieldState: { error } }) => (
                                <CompanyAutocomplete
                                    label={t('Target Company')}
                                    name="company_id"
                                    placeholder={t('Select Company')}
                                    defaultValue={selectedCompany}
                                    onSelect={(company) => {
                                        setSelectedCompany(company)
                                        field.onChange(company?.id ?? null)
                                        // Reset opportunity, address, contact when company changes
                                        setSelectedOpportunity(null)
                                        setValue('lead_id', null)
                                        setValue('address_id', null)
                                        setValue('contact_id', null)
                                        setNewCompanyAddresses([])
                                        setNewCompanyContacts([])
                                        if (company?.id) {
                                            dispatch(CompaniesAction.fetchCompanyByIdRaw(company.id))
                                                .then((fullCompany) => {
                                                    setNewCompanyAddresses(fullCompany.addresses || [])
                                                    setNewCompanyContacts(fullCompany.contacts || [])
                                                })
                                                .catch(() => {})
                                        }
                                    }}
                                    errors={error}
                                />
                            )}
                        />

                        {isDifferentCompany && (
                            <div className="flex flex-col gap-3 p-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
                                <div className="flex flex-col gap-1">
                                    <UiFormLabel className="text-blue-700 dark:text-blue-300">
                                        {t('Assign to existing Opportunity (optional)')}
                                    </UiFormLabel>
                                    <input
                                        type="text"
                                        className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder={t('Search opportunities...')}
                                        value={opportunitySearch}
                                        onChange={(e) => setOpportunitySearch(e.target.value)}
                                    />
                                    {opportunityResults.length > 0 ? (
                                        <div className="flex flex-col gap-1 max-h-40 overflow-y-auto">
                                            {opportunityResults.map((opp) => (
                                                <button
                                                    key={opp.id}
                                                    type="button"
                                                    onClick={() => {
                                                        setSelectedOpportunity(opp)
                                                        setValue('lead_id', opp.id)
                                                    }}
                                                    className={clsx(
                                                        'text-left text-sm px-3 py-2 rounded-lg border transition-colors',
                                                        selectedOpportunity?.id === opp.id
                                                            ? 'border-blue-500 bg-blue-100 dark:bg-blue-800 text-blue-900 dark:text-blue-100'
                                                            : 'border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800'
                                                    )}
                                                >
                                                    <span className="font-medium">{opp.name || opp.company_name || `#${opp.id}`}</span>
                                                    {opp.status && (
                                                        <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">({opp.status})</span>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-xs text-blue-600 dark:text-blue-400 italic">
                                            {selectedOpportunity
                                                ? `✓ ${t('Opportunity selected')}: ${selectedOpportunity.name || selectedOpportunity.company_name || `#${selectedOpportunity.id}`}`
                                                : t('No opportunity selected — a new one will be created automatically.')}
                                        </p>
                                    )}
                                </div>

                                <Controller
                                    control={control}
                                    name="address_id"
                                    render={({ field }) => {
                                        const addressOptions: UiSelectOption[] = newCompanyAddresses
                                            .filter((a) => !a.deleted_at)
                                            .map((a) => ({
                                                value: String(a.id),
                                                label: formatAddressSummary(a.street_name, a.postal_code, a.town) || a.name,
                                            }))
                                        return (
                                            <UiSelect
                                                label={t('Address')}
                                                options={addressOptions}
                                                value={field.value ? String(field.value) : null}
                                                onChange={(val) => field.onChange(val ? Number(val) : null)}
                                                placeholder={newCompanyAddresses.length === 0 ? t('No addresses available') : t('Select address')}
                                                disabled={newCompanyAddresses.length === 0}
                                            />
                                        )
                                    }}
                                />

                                <Controller
                                    control={control}
                                    name="contact_id"
                                    render={({ field }) => {
                                        const defaultContact = newCompanyContacts.find((c) => c.id === field.value) ?? null
                                        return (
                                            <CompanyContactAutocomplete
                                                label={t('Contact')}
                                                name="contact_id"
                                                placeholder={t('Select contact')}
                                                defaultValue={defaultContact}
                                                initialContacts={newCompanyContacts}
                                                onSelect={(contact) => field.onChange(contact?.id ?? null)}
                                            />
                                        )
                                    }}
                                />
                            </div>
                        )}

                        <div>
                            <UiFormLabel className="mb-2">{t('Calculations to copy')}</UiFormLabel>
                            <div className="flex flex-col gap-2 mt-2 max-h-60 overflow-auto border border-gray-200 dark:border-gray-800 rounded-lg p-3">
                                {selectedOffer?.calculations?.map((calc) => (
                                    <div key={calc.id} className="flex items-center gap-2">
                                        <UiCheckbox
                                            name={`calc-${calc.id}`}
                                            checked={calculationsFn?.includes(calc.id)}
                                            onChange={(e) => {
                                                const checked = e.target.checked
                                                const current = calculationsFn || []
                                                if (checked) {
                                                    setValue('calculations', [...current, calc.id])
                                                } else {
                                                    setValue(
                                                        'calculations',
                                                        current.filter((id) => id !== calc.id)
                                                    )
                                                }
                                            }}
                                            label={
                                                <span className="text-sm">
                                                    {calc.name || `${t('Calculation')} ${calc.id}`}
                                                    {calc.is_target && (
                                                        <span className="ml-2 text-xs text-blue-500 font-bold">
                                                            ({t('Target')})
                                                        </span>
                                                    )}
                                                </span>
                                            }
                                        />
                                    </div>
                                ))}
                                {(!selectedOffer?.calculations || selectedOffer.calculations.length === 0) && (
                                    <div className="text-gray-500 text-sm italic">{t('No calculations found')}</div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </UiFormInner>

            <UiFormActionsBar
                primaryText={isSubmitting ? t('Executing...') : t('Execute')}
                onPrimary={handleSubmit(onSubmitHandler, onErrorHandler)}
                loading={isSubmitting}
            />
        </form>
    )
}

export default BookingSellerDuplicateOfferModal
